import * as React from "react"
import { Container, Hidden, Visible } from "react-grid-system"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { useUpdateTerminalNickname } from "../../hooks/useUpdateTerminalNickname"
import LoadingSpinner from "../atoms/LoadingSpinner"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { theme } from "../theme"
import dayjs from "dayjs"
import H3 from "../atoms/typography/H3"
import H4 from "../atoms/typography/H4"
import SmallButton from "../atoms/buttons/SmallButton"
import { CheckmarkIcon } from "../atoms/icons/CheckboxIcons"
import { CrossIcon } from "../atoms/icons/CrossIcon"
import { EditIcon } from "../atoms/icons/EditIcon"
import TextInput from "./TextInput"
import { captureExceptionWithContext } from "../../utils/sentry"
import { ApolloError } from "@apollo/client"

interface TerminalInfoSectionProps {
  detailsData: {
    name: string
    suborganization: string
    id: string | number
    dataplan?: string
    serialNumber: string
    warrantyEndsDate: string
    modemName?: string
    modemSerialNumber: string
    sudoPassword: string
    canEditName?: boolean
  }
}

const DottedUnderline = styled.div`
  border-bottom: ${(props) => `1px dotted ${props.theme.colors.greyDark}`};
  margin-bottom: 2px;
  flex-grow: 1;
`

const DetailDataLine = styled.div`
  display: flex;
  padding: 0.5rem 0;
`

const DetailsSection = styled.div`
  padding: ${(props) => props.theme.spacings.large};
`

const NameAndEditSection = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`

const TerminalInfoSection: React.FC<TerminalInfoSectionProps> = ({ detailsData }) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = React.useState(false)
  const [isEditingTerminal, setIsEditingTerminal] = React.useState(false)
  const [editHasChanges, setEdisHasChanges] = React.useState(false)

  const onCompleted = () => console.log("updated terminal nickname")
  const onError = (error: ApolloError) => captureExceptionWithContext(error, "error updating terminal nickname")
  const { updateTerminalNickname } = useUpdateTerminalNickname({
    onCompleted,
    onError,
  })

  const [nickname, setNickname] = React.useState(detailsData.name)
  const onChangeNickname = (value: string) => {
    setNickname(value)
    setEdisHasChanges(true)
  }

  const onClickConfirm = async () => {
    if (nickname !== detailsData.name && nickname.length > 0) {
      setIsLoading(true)
      try {
        await updateTerminalNickname({
          variables: {
            id: detailsData.id,
            nickname: nickname,
          },
        })
        setIsLoading(false)
        setIsEditingTerminal(false)
        setEdisHasChanges(false)
      } catch (error) {
        captureExceptionWithContext(error, "error updating terminal nickname")
        setIsLoading(false)
      }
    }
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <Container>
      <HorizontalSpacer spacing={3} />
      <DetailsSection>
        {detailsData.canEditName && (
          <NameAndEditSection>
            <Hidden xs>
              <H4>
                {detailsData.name} {t("details")}
              </H4>
            </Hidden>
            <Visible xs>
              <H3>
                {detailsData.name} {t("details")}
              </H3>
            </Visible>
            {!isEditingTerminal && (
              <SmallButton
                icon={<EditIcon />}
                title="Edit"
                onClick={() => setIsEditingTerminal(true)}
                disabled={!detailsData?.canEditName}
              />
            )}
            {isEditingTerminal && (
              <div style={{ display: "flex" }}>
                <SmallButton
                  icon={<CheckmarkIcon />}
                  title={t("common.confirm")}
                  onClick={onClickConfirm}
                  disabled={!editHasChanges}
                />
                <Spacer spacing={1} />
                <SmallButton
                  icon={<CrossIcon color={theme.colors.greyDark} />}
                  title={t("common.cancel")}
                  onClick={() => setIsEditingTerminal(false)}
                />
              </div>
            )}
          </NameAndEditSection>
        )}
        <HorizontalSpacer spacing={2} />
        <DetailDataLine>
          <H3>{t("name")}</H3>
          <DottedUnderline />
          <H3>
            {isEditingTerminal && (
              <TextInput defaultValue={detailsData.name} onChange={(e) => onChangeNickname(e.target.value)} />
            )}
          </H3>
          {!isEditingTerminal && <H3>{detailsData.name}</H3>}
        </DetailDataLine>
        <DetailDataLine>
          <H3> {t("suborganization")}</H3>
          <DottedUnderline />
          {<H3>{detailsData.suborganization}</H3>}
        </DetailDataLine>
        {detailsData.dataplan !== undefined && (
          <DetailDataLine>
            <H3> {t("data_plan")}</H3>
            <DottedUnderline />
            <H3>{detailsData.dataplan}</H3>
          </DetailDataLine>
        )}
        <HorizontalSpacer spacing={2} />
        <DetailDataLine>
          <H3> {t("serial_number")}</H3>
          <DottedUnderline />
          <H3>{detailsData.serialNumber}</H3>
        </DetailDataLine>
        <DetailDataLine>
          <H3> {t("warranty_period")}</H3>
          <DottedUnderline />
          <H3>{dayjs(detailsData.warrantyEndsDate).format("DD MMM YYYY")}</H3>
        </DetailDataLine>
        <DetailDataLine>
          <H3> {t("modem")}</H3>
          <DottedUnderline />
          <H3>{detailsData.modemName}</H3>
        </DetailDataLine>
        <DetailDataLine>
          <H3> {t("modem_serial_number")}</H3>
          <DottedUnderline />
          <H3>{detailsData.modemSerialNumber}</H3>
        </DetailDataLine>
        <DetailDataLine>
          <H3> {t("sudo_password")}</H3>
          <DottedUnderline />
          <H3>{detailsData.sudoPassword}</H3>
        </DetailDataLine>
        <HorizontalSpacer spacing={3} />
      </DetailsSection>
    </Container>
  )
}

export default TerminalInfoSection
